import clsx from "clsx";
import {
  CheckCircle2,
  Circle,
  CircleAlert,
  CircleEllipsis,
  Clock,
} from "lucide-react";
import { FC } from "react";
import { useGetPipelineStatistics } from "~/api/documents/documents";
import { Progress } from "~/components/ui/progress";
import { Separator } from "~/components/ui/separator";

type DocumentPipelineStatisticsProps = {
  documentId: string;
  organizationId: string;
  projectId: string;
  pipelineStatus?: string;
};

type ItemProgressProps = {
  label: string;
  current: number;
  total: number;
  status?: "completed" | "in-progress" | "pending" | "error";
};

const getProgressStatus = (
  current: number,
  total: number
): "completed" | "in-progress" | "pending" => {
  if (current === 0) return "pending";
  if (current < total) return "in-progress";
  return "completed";
};

const getStageStatus = (
  stagePrefix: string,
  currentStatus: string,
  isError: boolean
): "completed" | "in-progress" | "pending" | "error" => {
  const status = currentStatus.toLowerCase();

  if (isError && status.includes(stagePrefix)) {
    return "error";
  }

  if (status.includes(`${stagePrefix}_completed`)) {
    return "completed";
  }

  if (status.includes(`${stagePrefix}_started`)) {
    return "in-progress";
  }

  return "pending";
};

const StatusIcon: FC<{
  status: "completed" | "in-progress" | "pending" | "error";
}> = ({ status }) => {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-3 w-3 text-green-500" />;
    case "in-progress":
      return <CircleEllipsis className="h-3 w-3 text-blue-500" />;
    case "error":
      return <CircleAlert className="h-3 w-3 text-red-500" />;
    case "pending":
    default:
      return <Clock className="h-3 w-3 text-zinc-400" />;
  }
};

const ItemProgress: FC<ItemProgressProps> = ({
  label,
  current,
  total,
  status: customStatus,
}) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  const status = customStatus || getProgressStatus(current, total);

  return (
    <div className="mb-3">
      <div className="flex justify-between text-xs mb-1">
        <div className="flex items-center gap-1.5">
          <StatusIcon status={status} />
          <span
            className={clsx(
              "font-medium",
              status === "completed" && "text-green-700",
              status === "in-progress" && "text-blue-700",
              status === "error" && "text-red-700",
              status === "pending" && "text-zinc-500"
            )}
          >
            {label}
          </span>
        </div>
        <span className="text-zinc-500">
          {current}/{total} ({percentage}%)
        </span>
      </div>
      <Progress
        value={percentage}
        className={clsx(
          "h-1.5",
          status === "completed" && "bg-green-100 [&>div]:bg-green-500",
          status === "in-progress" && "bg-blue-100 [&>div]:bg-blue-500",
          status === "error" && "bg-red-100 [&>div]:bg-red-500",
          status === "pending" && "bg-zinc-100 [&>div]:bg-zinc-300"
        )}
      />
    </div>
  );
};

export const DocumentPipelineStatistics: FC<
  DocumentPipelineStatisticsProps
> = ({ documentId, organizationId, projectId, pipelineStatus }) => {
  const { data: stats } = useGetPipelineStatistics(
    organizationId,
    projectId,
    documentId
  );

  if (!stats) {
    return null;
  }

  // Determine overall status
  const isError =
    stats.status === "ERROR" || stats.status.toLowerCase().includes("error");
  const isCompleted =
    stats.status === "COMPLETE" ||
    stats.status.toLowerCase().includes("complete");
  const isProcessing = !isError && !isCompleted;

  // Determine active AI processing stages
  const isChatDescActive = stats.chatContext?.isSummaryCompleted;
  const isChatContextActive = stats.chatContext?.isSnapshotCompleted;

  return (
    <div className="w-[300px] p-4">
      <div className="flex items-center gap-2 mb-4">
        <div
          className={clsx(
            "p-1.5 rounded-full",
            isError && "bg-red-100",
            isCompleted && "bg-green-100",
            isProcessing && "bg-blue-100"
          )}
        >
          {isError && <CircleAlert className="h-5 w-5 text-red-500" />}
          {isCompleted && <CheckCircle2 className="h-5 w-5 text-green-500" />}
          {isProcessing && <CircleEllipsis className="h-5 w-5 text-blue-500" />}
        </div>
        <div>
          <h4 className="text-sm font-medium">Document Processing</h4>
          <p className="text-xs text-zinc-500">
            Status:{" "}
            <span
              className={clsx(
                "font-medium",
                isError && "text-red-600",
                isCompleted && "text-green-600",
                isProcessing && "text-blue-600"
              )}
            >
              {stats.status}
            </span>
          </p>
        </div>
      </div>

      <h5 className="text-xs font-semibold text-zinc-700 mb-2">
        Content Extraction
      </h5>
      <ItemProgress
        label="Content extraction"
        current={stats.content.current}
        total={stats.content.total}
        status={
          isError && stats.content.current < stats.content.total
            ? "error"
            : undefined
        }
      />

      <Separator className="my-3" />

      {stats.pdf.total > 1 && (
        <>
          <h5 className="text-xs font-semibold text-zinc-700 mb-2">
            Page Analysis
          </h5>
          <ItemProgress
            label="Page continuation"
            current={stats.pageContinuation.current}
            total={stats.pageContinuation.total}
            status={
              isError &&
              stats.pageContinuation.current < stats.pageContinuation.total
                ? "error"
                : undefined
            }
          />
          <Separator className="my-3" />
        </>
      )}

      <h5 className="text-xs font-semibold text-zinc-700 mb-2">
        Section Processing
      </h5>
      <div className="flex justify-between text-xs text-zinc-600 mb-3">
        <div className="flex items-center gap-1.5">
          <Circle className="h-3 w-3 text-zinc-400" />
          <span>Total sections</span>
        </div>
        <span>{stats.sections}</span>
      </div>

      <ItemProgress
        label="Sections summarized"
        current={stats.summarized.current}
        total={stats.summarized.total}
        status={
          isError && stats.summarized.current < stats.summarized.total
            ? "error"
            : undefined
        }
      />
      <ItemProgress
        label="Sections classified"
        current={stats.classified.current}
        total={stats.classified.total}
        status={
          isError && stats.classified.current < stats.classified.total
            ? "error"
            : undefined
        }
      />

      <Separator className="my-3" />

      <h5 className="text-xs font-semibold text-zinc-700 mb-2">
        AI Processing
      </h5>
      <div className="space-y-2">
        <ItemProgress
          label="Chat Description"
          current={isChatDescActive ? 1 : 0}
          total={1}
          status={
            isChatDescActive
              ? "completed"
              : getStageStatus(
                  "chat_short_description",
                  pipelineStatus || stats.status,
                  isError
                )
          }
        />
        <ItemProgress
          label="Chat Context"
          current={isChatContextActive ? 1 : 0}
          total={1}
          status={
            isChatContextActive
              ? "completed"
              : getStageStatus(
                  "chat_context",
                  pipelineStatus || stats.status,
                  isError
                )
          }
        />
      </div>
    </div>
  );
};
